import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import type { PaymentRequestAdapters } from '../../../module/order/orderAdapters';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/payment-requests/{hash}/check';
const HTTP_METHOD = 'get';

export type MakeGetPaymentRequestByHashCheckDeps<PRType> = {
  readonly paymentRequestExtended: Schema.Codec<PRType, unknown>;
  readonly paymentRequestAdapters: PaymentRequestAdapters<StripJsonLd<PRType>>;
};

export const makeGetPaymentRequestByHashCheck = <PRType>(
  deps: MakeGetPaymentRequestByHashCheckDeps<PRType>
) => {
  const CheckPaymentRequestByHashError = createBackendApiError({
    domain: 'Order',
    endpoint: 'CheckPaymentRequestByHash',
    path: ENDPOINT,
    identifierField: 'hash',
  } as const);

  const getPaymentRequestCheckFetch = (hash: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { hash } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'paymentRequest.hash': hash,
        },
      })
    );

  const getPaymentRequestByHashCheck = (hash: string) =>
    getPaymentRequestCheckFetch(hash).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.paymentRequestExtended)(response),
          404: (response) =>
            Effect.fail(new CheckPaymentRequestByHashError.NotFoundError({ hash, response })),
          orElse: (response) =>
            Effect.fail(new CheckPaymentRequestByHashError.ApiRequestError({ hash, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.PaymentRequestCheck,
          'Debug',
          `Successfully checked payment request for hash: ${hash} -> state: ${deps.paymentRequestAdapters.state(result)}`
        )
      ),
      Effect.withSpan('api.paymentRequest.check', {
        kind: 'internal',
        attributes: {
          'paymentRequest.hash': hash,
        },
      })
    );

  return { getPaymentRequestByHashCheck, CheckPaymentRequestByHashError };
};
